import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  AccountBalance as DepositIcon
} from '@mui/icons-material';
import { bookingServiceV2 } from '@/services/bookingService.v2';
import type { BookingV2 } from '@/types';

interface ReleaseDepositDialogProps {
  open: boolean;
  onClose: () => void;
  booking: BookingV2 | null;
  onSuccess: () => void;
}

export const ReleaseDepositDialog: React.FC<ReleaseDepositDialogProps> = ({
  open,
  onClose,
  booking,
  onSuccess
}) => {
  const [releaseType, setReleaseType] = useState<'full' | 'partial' | 'forfeit'>('full');
  const [chargedAmount, setChargedAmount] = useState<string>('');
  const [damageDescription, setDamageDescription] = useState('');
  const [damagePhotos, setDamagePhotos] = useState<string>('');
  const [forfeitReason, setForfeitReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const depositAmount = booking ? parseFloat(booking.securityDepositAmount || booking.securityDeposit || '0') : 0;

  const handleChargedAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setChargedAmount(value);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (releaseType === 'partial') {
      const charged = parseFloat(chargedAmount);
      if (!chargedAmount || charged <= 0) {
        setError('Please enter a valid charged amount');
        return;
      }
      if (charged > depositAmount) {
        setError(`Charged amount cannot exceed deposit (₦${depositAmount.toLocaleString()})`);
        return;
      }
      if (!damageDescription.trim()) {
        setError('Please describe the damage');
        return;
      }
    }

    if (releaseType === 'forfeit' && !forfeitReason.trim()) {
      setError('Please provide a reason for forfeiting the deposit');
      return;
    }

    if (!booking?.id) {
      setError('Invalid booking');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const releaseData: any = {
        releaseType,
        adminNotes: adminNotes.trim() || undefined
      };

      if (releaseType === 'partial') {
        releaseData.chargedAmount = parseFloat(chargedAmount);
        releaseData.damageDescription = damageDescription.trim();
        if (damagePhotos.trim()) {
          releaseData.damagePhotos = damagePhotos.split(',').map(url => url.trim());
        }
      }

      if (releaseType === 'forfeit') {
        releaseData.forfeitReason = forfeitReason.trim();
      }

      await bookingServiceV2.releaseDeposit(booking.id, releaseData);

      onSuccess();
      handleClose();

    } catch (err: any) {
      setError(err.message || 'Failed to release deposit');
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setReleaseType('full');
    setChargedAmount('');
    setDamageDescription('');
    setDamagePhotos('');
    setForfeitReason('');
    setAdminNotes('');
    setError(null);
    onClose();
  };

  const calculateRefund = () => {
    if (releaseType === 'full') return depositAmount;
    if (releaseType === 'partial') {
      const charged = parseFloat(chargedAmount) || 0;
      return depositAmount - charged;
    }
    return 0;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DepositIcon color="primary" />
            <Typography variant="h6">Release Security Deposit</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Booking info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Booking:</strong> {booking?.bookingNumber}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            <strong>Deposit Amount:</strong> ₦{depositAmount.toLocaleString()}
          </Typography>
        </Alert>

        {/* Release Type Selection */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Select Release Type
        </Typography>
        <RadioGroup
          value={releaseType}
          onChange={(e) => setReleaseType(e.target.value as 'full' | 'partial' | 'forfeit')}
          sx={{ mb: 3 }}
        >
          <FormControlLabel
            value="full"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Full Release (No Damage)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Refund entire deposit to customer
                  </Typography>
                </Box>
              </Box>
            }
          />
          <FormControlLabel
            value="partial"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Partial Release (Minor Damage)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Deduct damage cost, refund remaining
                  </Typography>
                </Box>
              </Box>
            }
          />
          <FormControlLabel
            value="forfeit"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BlockIcon color="error" sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Forfeit (Major Damage)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Keep entire deposit, no refund
                  </Typography>
                </Box>
              </Box>
            }
          />
        </RadioGroup>

        {/* Partial Release Fields */}
        {releaseType === 'partial' && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Damage Details
            </Typography>

            <TextField
              label="Charged Amount"
              required
              fullWidth
              value={chargedAmount}
              onChange={handleChargedAmountChange}
              placeholder="0.00"
              InputProps={{
                startAdornment: <InputAdornment position="start">₦</InputAdornment>,
              }}
              helperText={`Maximum: ₦${depositAmount.toLocaleString()}`}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Damage Description"
              required
              fullWidth
              multiline
              rows={3}
              value={damageDescription}
              onChange={(e) => setDamageDescription(e.target.value)}
              placeholder="e.g., Rear bumper scratch - repair cost"
              sx={{ mb: 2 }}
            />

            <TextField
              label="Damage Photos (URLs)"
              fullWidth
              value={damagePhotos}
              onChange={(e) => setDamagePhotos(e.target.value)}
              placeholder="https://..., https://..."
              helperText="Comma-separated URLs (optional)"
            />
          </Box>
        )}

        {/* Forfeit Fields */}
        {releaseType === 'forfeit' && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Forfeit Reason
            </Typography>

            <TextField
              label="Reason for Forfeiting Deposit"
              required
              fullWidth
              multiline
              rows={3}
              value={forfeitReason}
              onChange={(e) => setForfeitReason(e.target.value)}
              placeholder="e.g., Major damage to vehicle interior - repair exceeds deposit"
            />
          </Box>
        )}

        {/* Admin Notes */}
        <TextField
          label="Admin Notes (Internal)"
          fullWidth
          multiline
          rows={2}
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          placeholder="Internal notes for record keeping..."
          helperText="Optional - Only visible to admins"
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }} />

        {/* Summary */}
        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Summary
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Deposit Amount</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ₦{depositAmount.toLocaleString()}
            </Typography>
          </Box>
          {releaseType === 'partial' && chargedAmount && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="error">
                Damage Charge
              </Typography>
              <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                -₦{parseFloat(chargedAmount).toLocaleString()}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {releaseType === 'forfeit' ? 'Amount Kept' : 'Refund Amount'}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: releaseType === 'forfeit' ? 'error.main' : 'success.main' }}>
              ₦{calculateRefund().toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={processing}
        >
          {processing ? 'Processing...' : 'Release Deposit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
